import type { ButtonSize } from "@replo/design-system/components/button/button-shared";
import type { PendingShopifyIntegrationCookie } from "schemas/cookies";
import type { ReploSimpleProject } from "schemas/generated/project";

import * as React from "react";

import { ShopifyIntegrationSelectable } from "@editor/components/dashboard/projects/ShopifyIntegrationSelectable";
import { useShopifyIntegrationConnect } from "@editor/components/editor/page/useShopifyIntegrationConnect";
import SettingsCard from "@editor/components/shops-ui/settings/SettingsCard";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import { useModal } from "@editor/hooks/useModal";

import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { Link2Off } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

const ConnectionsCta: React.FC<{
  cookieValue: PendingShopifyIntegrationCookie;
  className?: string;
  selectedProject?: ReploSimpleProject;
  size?: ButtonSize;
  ctaText?: string;
}> = ({
  cookieValue,
  className,
  selectedProject,
  size = "base",
  ctaText = "Add Shopify Integration",
}) => {
  const workspaceId = useCurrentWorkspaceId();
  const { project: loadableProject } = useCurrentProjectContext();
  const project = selectedProject ?? loadableProject;

  const {
    selectedIntegrationId,
    setSelectedIntegrationId,
    isLoading,
    handleConnect,
  } = useShopifyIntegrationConnect({
    workspaceId,
    projectId: project?.id ?? "",
    cookieValue,
  });

  // NOTE (Matt 2024-09-17): Given that this component can be used to connect an integration to a project or to add a new integration,
  // we set `allowExistingIntegrations` to determine whether or not to show the existing integrations selectable.
  const allowExistingIntegrations = exhaustiveSwitch({
    type: cookieValue.type,
  })({
    sections: true,
    projectSettings: true,
    publish: true,
    productPicker: true,
    assets: true,
    integrationHub: false,
    billingModal: false,
    noShopifyErrorModal: false,
    preview: true,
    onboarding: false,
    newProject: false,
    shopStyles: true,
    shopSettingsConnection: false,
    projectView: false,
  });

  return (
    <div className="flex flex-col gap-4">
      {allowExistingIntegrations && (
        <ShopifyIntegrationSelectable
          isDisabled={isLoading}
          workspaceId={workspaceId}
          selectedIntegrationId={selectedIntegrationId}
          handleChangeSelection={setSelectedIntegrationId}
        />
      )}
      <Button
        variant="primary"
        size={size}
        layoutClassName="w-full"
        UNSAFE_className={className}
        onClick={(e) => {
          void handleConnect(e);
        }}
        isLoading={isLoading}
        disabled={
          allowExistingIntegrations && (!selectedIntegrationId || isLoading)
        }
      >
        {ctaText}
      </Button>
    </div>
  );
};

const Connections = () => {
  const { project } = useCurrentProjectContext();
  const { openModal } = useModal();
  const shopifyData = project?.integrations?.shopify?.store;
  return (
    <SettingsCard title="Shopify" className="w-full max-w-[616px]">
      <div className="flex flex-col gap-1.5 w-full">
        <span className="text-base font-medium">Shopify Integration</span>
        {shopifyData ? (
          <div className="flex bg-sheet items-center justify-between p-3 border-0.5 rounded-lg border-border">
            <span className="text-base font-medium">
              {project?.integrations?.shopify?.store?.shopifyUrl}
            </span>
            <div className="flex items-center gap-2">
              <IconButton
                variant="secondary"
                icon={<Link2Off size={12} />}
                size="sm"
                tooltipText="Disconnect Shopify Store"
                onClick={() => {
                  openModal({
                    type: "removeShopifyIntegrationModal",
                    props: {
                      projectId: project?.id ?? "",
                      integrationId: shopifyData.integrationId ?? "",
                    },
                  });
                }}
              />
              <Tag
                variant="success"
                size="base"
                startEnhancer={
                  <img
                    src="/images/shopify-logo.svg"
                    alt="Shopify logo"
                    className="w-4 h-4"
                  />
                }
              >
                Connected
              </Tag>
            </div>
          </div>
        ) : (
          <ConnectionsCta
            cookieValue={{
              type: "shopSettingsConnection",
              projectId: project?.id ?? "",
              workspaceId: project?.ownerWorkspaceId ?? "",
            }}
          />
        )}
      </div>
    </SettingsCard>
  );
};
export default Connections;
